import { apiFetch } from "./client";
import type { TaskDetailResponse } from "./types";

/**
 * Client360 — Этап 3A «Task Core» API.
 *
 * Holding-context task lifecycle (create / complete-with-outcome / quick call-log /
 * timeline) + the role-limited responsible options (Q3). Mounts on the same
 * /Custom/frontend/* contract as F3 but under the /client360/* namespace.
 */

export type TaskSubtype = "generic" | "call" | "follow_up" | "bi_signal";

export type TaskOutcome =
  | "contacted" | "not_reached" | "promised_payment" | "order_expected" | "needs_follow_up"
  | "completed_successfully" | "blocked" | "not_needed"
  | "acted" | "dismissed_with_reason" | "converted_to_follow_up"
  | "other";

/** Outcome options per subtype (mirror of backend §2.3). */
export const OUTCOMES_BY_SUBTYPE: Record<TaskSubtype, TaskOutcome[]> = {
  call: ["contacted", "not_reached", "promised_payment", "order_expected", "needs_follow_up", "other"],
  generic: ["completed_successfully", "blocked", "needs_follow_up", "not_needed", "other"],
  follow_up: ["completed_successfully", "blocked", "needs_follow_up", "not_needed", "other"],
  bi_signal: ["acted", "dismissed_with_reason", "converted_to_follow_up", "other"],
};

export const OUTCOME_LABEL: Record<TaskOutcome, string> = {
  contacted: "Зв'язались",
  not_reached: "Не додзвонились",
  promised_payment: "Обіцянка оплати",
  order_expected: "Очікується замовлення",
  needs_follow_up: "Потрібен follow-up",
  completed_successfully: "Виконано успішно",
  blocked: "Заблоковано",
  not_needed: "Не потрібно",
  acted: "Відреаговано",
  dismissed_with_reason: "Відхилено з причиною",
  converted_to_follow_up: "Переведено у follow-up",
  other: "Інше",
};

/** outcomes that require a note. */
export const OUTCOME_NOTE_REQUIRED: TaskOutcome[] = ["other", "blocked", "not_needed"];

export interface ResponsibleOption { id: string; name: string }
export interface ResponsibleOptionsResponse {
  options: ResponsibleOption[];
  defaultId: string;
  /** true → manager scope (only self) — render select as read-only. */
  restricted: boolean;
}

export interface PaymentPromiseInput {
  promisedPaymentDate: string; // YYYY-MM-DD
  amount?: number | null;
  note?: string;
  createFollowUp?: boolean;
}

export interface Client360TaskCreatePayload {
  holdingId: string;
  title: string;
  subtype: TaskSubtype;
  dueDate: string; // YYYY-MM-DD (required)
  priority?: "high" | "medium" | "low";
  assigneeId?: string;
  contactId?: string;
  legalEntityId?: string;
  description?: string;
  /** TASK-ATT v1.3 (W1/W5) — bound attachment ids (full merged list). */
  attachmentsIds?: string[];
  /** D-142.W1 (П-6) — спостерігачі create-time (атомарно з задачею, ≤30). */
  watchersIds?: string[];
}

export interface Client360TaskCompletePayload {
  outcome: TaskOutcome;
  outcomeNote?: string;
  nextActionDecision?: "no_follow_up" | "create_follow_up" | "schedule_meeting" | "create_internal_request";
  paymentPromise?: PaymentPromiseInput;
  followUp?: { dueDate: string; title?: string };
}

export interface Client360QuickLogPayload {
  holdingId: string;
  outcome: TaskOutcome;
  outcomeNote?: string;
  contactId?: string;
  legalEntityId?: string;
  paymentPromise?: PaymentPromiseInput;
  followUp?: { dueDate: string; title?: string };
}

export interface CreatedArtifact { type: "payment_promise" | "follow_up"; id: string }
export interface Client360TaskMutationResponse {
  task: TaskDetailResponse;
  created: CreatedArtifact[];
}

export type TimelineKind =
  | "task_created" | "task_completed" | "task_postponed" | "task_cancelled"
  | "call_logged" | "payment_promise_created" | "follow_up_created"
  | "meeting" | "meeting_report";

export interface TimelineEvent {
  id: string;
  kind: TimelineKind;
  title: string;
  detail: string;
  at: string;
  tone: "green" | "amber" | "blue" | "gray";
  result: string | null;
  source: { entityType: "task" | "payment_promise" | "meeting" | "interaction" | "internal_request"; entityId: string };
  /** 3C — meeting awaiting a report (drives the «Заповнити звіт →» inline CTA). */
  reportRequired?: boolean;
  /**
   * D-150 Q1 — server-derived open-gate (backend read predicate exported to the
   * event). «Відкрити» renders ONLY when true; undefined/false = no CTA (§0 —
   * no dead links, no guaranteed-403 buttons).
   */
  canOpen?: boolean;
}

export interface TimelineResponse {
  items: TimelineEvent[];
  total: number;
  hasMore: boolean;
  nextOffset: number | null;
}

export function fetchResponsibleOptions(signal?: AbortSignal): Promise<ResponsibleOptionsResponse> {
  return apiFetch<ResponsibleOptionsResponse>(
    "/Custom/frontend/client360/task-responsible-options",
    { signal },
  );
}

export function createClient360Task(body: Client360TaskCreatePayload): Promise<TaskDetailResponse> {
  return apiFetch<TaskDetailResponse>("/Custom/frontend/client360/tasks", {
    method: "POST",
    body,
  });
}

export function completeClient360Task(
  id: string,
  body: Client360TaskCompletePayload,
): Promise<Client360TaskMutationResponse> {
  return apiFetch<Client360TaskMutationResponse>(
    `/Custom/frontend/client360/tasks/${encodeURIComponent(id)}/complete`,
    { method: "POST", body },
  );
}

export function quickLogCall(body: Client360QuickLogPayload): Promise<Client360TaskMutationResponse> {
  return apiFetch<Client360TaskMutationResponse>(
    "/Custom/frontend/client360/calls/quick-log",
    { method: "POST", body },
  );
}

export function fetchTimeline(
  holdingId: string,
  params: { limit?: number; offset?: number } = {},
  signal?: AbortSignal,
): Promise<TimelineResponse> {
  return apiFetch<TimelineResponse>(
    `/Custom/frontend/client360/${encodeURIComponent(holdingId)}/timeline`,
    { query: { limit: params.limit, offset: params.offset }, signal },
  );
}
